import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon, TrashIcon, CloudArrowUpIcon, DocumentIcon, EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { fetchDocuments, deleteDocument } from '../../store/slices/documentSlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, Button, Badge, SearchInput, Pagination, ConfirmDialog, Card, Modal, FileUpload } from '../../components/Common';
import { uploadAPI } from '../../services/api';

const DocumentList = () => {
  const dispatch = useDispatch();
  const { documents, loading, pagination } = useSelector((state) => state.document);
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [uploadModal, setUploadModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [previewModal, setPreviewModal] = useState({ open: false, document: null });
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    dispatch(fetchDocuments({ page, size: pageSize, search }));
  }, [dispatch, page, pageSize, search]);

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      await uploadAPI.upload(formData);
      dispatch(showSuccess('File uploaded successfully'));
      setUploadModal(false);
      setUploadFile(null);
      dispatch(fetchDocuments({ page, size: pageSize, search }));
    } catch (error) {
      dispatch(showError(error.response?.data?.message || 'Failed to upload file'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteDocument(deleteModal.id)).unwrap();
        dispatch(showSuccess('Document deleted successfully'));
        dispatch(fetchDocuments({ page, size: pageSize, search }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete document'));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  const isImage = (url, type) => {
    if (type?.toLowerCase().includes('image')) return true;
    if (url) {
      const ext = url.split('.').pop()?.toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
    }
    return false;
  };

  const columns = [
    {
      key: 'name',
      title: 'Document',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          {row.url && isImage(row.url, row.type) ? (
            <img src={row.url} alt="" className="w-10 h-10 rounded object-cover" />
          ) : (
            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
              <DocumentIcon className="w-5 h-5 text-slate-400" />
            </div>
          )}
          <div>
            <p className="font-medium text-slate-800 line-clamp-1">{value || row.uid || 'Unnamed'}</p>
            <p className="text-xs text-slate-500">{row.type || 'File'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'uid',
      title: 'UID',
      render: (value) => <span className="text-slate-600 text-sm font-mono">{value || '-'}</span>,
    },
    {
      key: 'type',
      title: 'Type',
      render: (value) => <Badge variant="info">{value || 'Unknown'}</Badge>,
    },
    {
      key: 'status',
      title: 'Status',
      render: (value, row) => (
        <Badge variant={row.status_id !== 2 ? 'success' : 'danger'} dot>
          {row.status_id !== 2 ? 'Active' : 'Deleted'}
        </Badge>
      ),
    },
    {
      key: 'created_on',
      title: 'Created',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-1">
          {row.url && (
            <>
              <button onClick={() => setPreviewModal({ open: true, document: row })} className="p-1.5 hover:bg-slate-100 rounded" title="Preview">
                <EyeIcon className="w-4 h-4 text-slate-500" />
              </button>
              <a href={row.url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-slate-100 rounded" title="Download">
                <ArrowDownTrayIcon className="w-4 h-4 text-slate-500" />
              </a>
            </>
          )}
          <button onClick={() => setDeleteModal({ open: true, id: row.id })} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Documents</h1>
          <p className="text-slate-500">Manage uploaded files and documents</p>
        </div>
        <Button icon={PlusIcon} onClick={() => setUploadModal(true)}>Upload File</Button>
      </div>

      <Card noPadding>
        <div className="p-4 border-b border-slate-100">
          <SearchInput value={search} onChange={setSearch} placeholder="Search documents..." className="max-w-md" />
        </div>

        <Table columns={columns} data={documents} loading={loading} emptyMessage="No documents found" emptyIcon={CloudArrowUpIcon} />

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      <Modal isOpen={uploadModal} onClose={() => setUploadModal(false)} title="Upload File" size="md"
        footer={<><Button variant="secondary" onClick={() => setUploadModal(false)}>Cancel</Button><Button onClick={handleUpload} loading={uploading} disabled={!uploadFile}>Upload</Button></>}>
        <FileUpload label="Select file to upload" accept="*/*" maxSize={10} onChange={setUploadFile} />
      </Modal>

      <Modal isOpen={previewModal.open} onClose={() => setPreviewModal({ open: false, document: null })} title="File Preview" size="lg">
        {previewModal.document && (
          <div className="text-center">
            {isImage(previewModal.document.url, previewModal.document.type) ? (
              <img src={previewModal.document.url} alt={previewModal.document.name} className="max-w-full max-h-96 mx-auto rounded-lg" />
            ) : (
              <div className="p-12 bg-slate-100 rounded-xl">
                <DocumentIcon className="w-24 h-24 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">Preview not available</p>
                <a href={previewModal.document.url} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline mt-2 inline-block">Open in new tab</a>
              </div>
            )}
            <p className="mt-4 font-medium text-slate-800">{previewModal.document.name || previewModal.document.uid}</p>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={handleDelete} title="Delete Document" message="Are you sure you want to delete this document?" type="danger" confirmText="Delete" />
    </div>
  );
};

export default DocumentList;
